import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import Button from "@/components/foundation/Button";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import RoadmapCard from "@/components/marketing/RoadmapCard";
import PartnerLink, { AutoLinkPartners } from "@/components/marketing/PartnerLink";

const PRINCIPLES = [
  { title: "Cash flow beats credit history", body: "Historical debt data was never designed to answer whether a business is generating momentum today." },
  { title: "Verified data beats gut feel", body: "Bank-sourced signals produce better underwriting decisions than self-reported spreadsheets." },
  { title: "Modern businesses deserve modern infrastructure", body: "The tooling for capital should feel like the rest of the software modern operators already run." },
];

const TECH_PARTNERS = [
  {
    name: "Mastercard Finicity",
    partner: "finicity",
    role: "Secure open banking",
    body: "Mastercard Finicity powers ZenMe's bank connectivity. It's one of the most trusted open-banking rails available today, used by leading financial institutions across North America. All merchant banking data flows through Finicity's secure, read-only integration.",
  },
  {
    name: "Uptiq AI",
    partner: "uptiq",
    role: "AI/ML infrastructure",
    body: "Uptiq AI provides the AI/ML engineering, scalable cloud, and platform infrastructure that underlie ZenScore. Our proprietary scoring layer is built on top of Uptiq's data and model pipelines, allowing ZenMe to focus on the underwriting intelligence itself.",
  },
  {
    name: "ZenScore",
    role: "Proprietary intelligence layer",
    body: "ZenScore is ZenMe's own proprietary funding readiness signal, built on top of Mastercard Finicity's data and Uptiq AI's infrastructure. It's how live business activity gets translated into a transparent, category-broken-down score that drives every funding decision.",
  },
];

const ROADMAP_PHASES = [
  { phase: "01", title: "Foundation", status: "live", features: ["Merchant onboarding", "Mastercard Finicity integration", "ZenScore engine", "Funding applications", "Admin platform"].map((s) => <AutoLinkPartners key={s} text={s} />) },
  { phase: "02", title: "Expansion", status: "in-development", features: ["Repayment monitoring", "ZenScore validation loops", "Funding partner portal", "Marketplace beta", "SaaS platform"] },
  { phase: "03", title: "Ecosystem", status: "vision", features: ["Open marketplace", "Embedded finance", "Automated underwriting", "Partner subscriptions", "Capital APIs & national expansion"] },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · About"
        description="ZenMe Capital is building the AI-powered infrastructure between businesses and funding partners — powered by Mastercard Finicity, Uptiq AI, and our proprietary ZenScore."
        path="/about"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-24"
        data-testid="about-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb color="emerald" size={480} intensity={0.18} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="max-w-3xl">
            <FadeIn>
              <Badge variant="emerald" pulse>About</Badge>
            </FadeIn>
            <FadeIn delay={0.06}>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                Making business capital more{" "}
                <span className="text-brand-emerald">
                  intelligent, transparent, and data-driven.
                </span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.14}>
              <p className="mt-6 max-w-2xl text-base text-text-secondary sm:text-lg leading-relaxed">
                ZenMe Capital is building the AI-powered infrastructure between
                businesses and funding partners.
              </p>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* MISSION */}
      <Section
        eyebrow="Our Mission"
        headline="Rebuild business capital from the data up."
      >
        <div className="mx-auto max-w-3xl">
          <FadeIn>
            <p className="text-base leading-relaxed text-text-secondary sm:text-lg">
              We&rsquo;re a technology company first. Everything ZenMe builds
              starts with the question: can real, verified financial data lead
              to a better decision than the one traditional underwriting would
              have made?
            </p>
          </FadeIn>
          <FadeIn delay={0.08}>
            <p className="mt-6 text-base leading-relaxed text-text-secondary sm:text-lg">
              ZenScore is the answer we&rsquo;re building. It&rsquo;s a
              transparent, category-broken-down funding readiness signal that
              reflects how businesses actually operate — not just what they
              looked like on last year&rsquo;s tax return. Funding is what
              comes on top of that intelligence, not the other way around.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* WHAT WE BELIEVE */}
      <Section
        eyebrow="What We Believe"
        headline="Three principles behind every product decision."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {PRINCIPLES.map((p, i) => (
            <FadeIn key={p.title} delay={0.06 * i}>
              <Card variant="elevated" className="flex h-full flex-col gap-4 p-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-emerald">
                  Principle {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-xl font-semibold text-white">
                  {p.title}
                </h3>
                <p className="text-sm leading-relaxed text-text-secondary">
                  {p.body}
                </p>
              </Card>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* TECH PARTNERS */}
      <Section
        eyebrow="Our Technology Partners"
        headline="The stack behind ZenScore."
        subheadline="ZenMe is deliberately built on top of best-in-class infrastructure so we can focus our engineering on the intelligence layer itself."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {TECH_PARTNERS.map((t, i) => (
            <FadeIn key={t.name} delay={0.08 * i}>
              <Card variant="elevated" className="flex h-full flex-col gap-4 p-8">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-emerald">
                    {t.role}
                  </span>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-emerald shadow-[0_0_8px_rgba(0,230,118,0.7)]" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-white">
                  {t.partner ? (
                    <PartnerLink partner={t.partner} showGlyph={false} className="!text-white hover:!text-brand-emerald">
                      {t.name}
                    </PartnerLink>
                  ) : (
                    t.name
                  )}
                </h3>
                <p className="text-sm leading-relaxed text-text-secondary">
                  <AutoLinkPartners text={t.body} />
                </p>
              </Card>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* ROADMAP TEASER */}
      <Section
        eyebrow="Roadmap"
        headline="Where we are, and where we're going."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {ROADMAP_PHASES.map((p, i) => (
            <RoadmapCard key={p.phase} {...p} index={i} />
          ))}
        </div>
      </Section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden py-24 sm:py-32">
        <GlowOrb color="emerald" size={520} intensity={0.2} className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        <Container className="relative z-10">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center">
            <FadeIn>
              <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">
                Want to work with us?
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-base text-text-secondary">
                We&rsquo;d love to hear from you — whether that&rsquo;s a
                partnership conversation, a product question, or a career
                interest.
              </p>
            </FadeIn>
            <FadeIn delay={0.18}>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate("/contact")}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                >
                  Contact
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => (window.location.href = "mailto:careers@zenmecapital.com")}
                >
                  Careers
                </Button>
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>
    </PageLayout>
  );
}
